import { useQuery } from '@tanstack/react-query';
import { useClientConfig } from '@/components/client-logo';
import { configFileQuery } from '@/features/configuration/configuration.api';
import { useAuthStore } from '@/features/auth/auth.store';

/**
 * Per-client and per-store switches the sidebar gates on that are *not* roles
 * and *not* environment config.
 *
 * The original sidebar read these inline from three different sources — the
 * `web/config` file (`config?.contents?.data?.hasPriceDb`, and in two places
 * the doubled-up `config?.contents?.data?.data?.hasLoyalty`), the selected
 * business object (`business?.hasLayaway`), and the login state blob
 * (`currentState.globalReqSideAddons`). Naming them here means a nav entry can
 * declare `flag: 'hasPriceDb'` instead of carrying a chain of optional lookups,
 * and there is one place to fix when the shapes disagree.
 */
export const CLIENT_FLAGS = [
  /** Business config: a price database is attached, enabling item-price pages. */
  'hasPriceDb',
  /** Business config: loyalty programme enabled. */
  'hasLoyalty',
  /** Business config: FAQ/knowledge base published. */
  'hasFaq',
  /** Selected store sells layaway. */
  'hasLayaway',
  /** Selected store offers delivery at its primary location. */
  'isDeliveryAvailable',
  /** Selected store has 3PL delivery enabled. */
  'allowDelivery',
  /** Login state: required sides/add-ons are enabled globally AND managed here. */
  'managesReqSideAddons',
  /** Server config: the booking API sells gift vouchers for this client. */
  'hasBookingVouchers',
] as const;

export type ClientFlag = (typeof CLIENT_FLAGS)[number];

export type ClientFlags = Record<ClientFlag, boolean>;

/**
 * The booking API refuses every voucher endpoint unless this is on, and only
 * MECA has it on. The portal reads the same key from `server.json` — the file
 * the Configuration page edits — so one switch drives both.
 */
const BOOKING_VOUCHERS_KEY = 'Gopanza:BookingVouchersEnabled';

/** `server.json` is hand-edited; accept `true` and `"true"` alike. */
function readBookingVouchers(text: string | null | undefined): boolean {
  if (!text) return false;
  try {
    const config = JSON.parse(text) as Record<string, unknown>;
    const value = config[BOOKING_VOUCHERS_KEY];
    return value === true || String(value).toLowerCase() === 'true';
  } catch {
    return false;
  }
}

const ALL_ENABLED = Object.fromEntries(
  CLIENT_FLAGS.map((flag) => [flag, true])
) as ClientFlags;

/**
 * A flag whose source has not been ported yet stays visible, so the whole tree
 * can still be walked. A flag whose source *is* available is answered from it —
 * including while the config file is still loading, when the honest answer is
 * "not yet", not "yes".
 */
export function useClientFlags(): ClientFlags {
  const business = useAuthStore((s) => s.business);
  const state = useAuthStore((s) => s.state);
  const { data: config, isSuccess } = useClientConfig();
  const serverConfig = useQuery({
    ...configFileQuery('server'),
    select: readBookingVouchers,
    // A switch flipped on the Configuration page lands through the shared
    // cache; otherwise it only needs to be fresh per session.
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  // Both halves have to be true: the platform enables required sides and
  // add-ons, and this client is the one that maintains them.
  const managesReqSideAddons =
    !!state?.globalReqSideAddons && !!state?.managesReqSideAddons;

  return {
    ...ALL_ENABLED,
    // `web/config` — the same file the header logo comes from.
    hasPriceDb: isSuccess ? !!config?.hasPriceDb : false,
    // `server.json`. Off while loading or unreadable, which is the API's
    // default too.
    hasBookingVouchers: serverConfig.data ?? false,
    hasLoyalty: isSuccess ? !!config?.hasLoyalty : false,
    hasFaq: isSuccess ? !!config?.hasFaq : false,
    // Only meaningful once the login response has been seen.
    managesReqSideAddons: state ? managesReqSideAddons : false,
    hasLayaway: business !== null ? ALL_ENABLED.hasLayaway : true,
    // The selected store's own switch, as the original sidebar read it.
    allowDelivery: !!business?.allowDelivery,
  };
}
